import type { NoteType, PurposeChoice, ReportInputWave2, TripScope } from './types';

export type NewReportForm = {
  title: string;
  purposeId: string;
  noteType: NoteType | '';
  month: string;
  tripStart: string;
  tripEnd: string;
  tripScope: TripScope | '';
};

export function emptyForm(month: string): NewReportForm {
  return {
    title: '',
    purposeId: '',
    noteType: '',
    month,
    tripStart: '',
    tripEnd: '',
    tripScope: '',
  };
}

/** `count` months from «2026-10» on, the first one included: the note may be for a later month. */
export function monthsFrom(month: string, count = 7): string[] {
  const [year, number] = month.split('-').map(Number);
  if (year === undefined || number === undefined) return [month];
  return Array.from({ length: count }, (_, step) => {
    const date = new Date(year, number - 1 + step, 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  });
}

/** The type the note will have: the one chosen, or the purpose's only one (M18 allows both). */
export function resolvedType(
  purpose: PurposeChoice | undefined,
  chosen: NewReportForm['noteType'],
): NoteType | null {
  if (!purpose) return null;
  if (purpose.noteTypes.length === 1) return purpose.noteTypes[0] ?? null;
  return chosen === '' ? null : chosen;
}

/** True when the form holds everything the server asks for. */
export function isComplete(form: NewReportForm, purpose: PurposeChoice | undefined): boolean {
  if (form.title.trim() === '' || !purpose) return false;
  const type = resolvedType(purpose, form.noteType);
  if (type === null) return false;
  if (type === 'reimbursement') return true;
  return (
    form.tripStart !== '' &&
    form.tripEnd !== '' &&
    form.tripEnd >= form.tripStart &&
    form.tripScope !== ''
  );
}

export function toInput(
  form: NewReportForm,
  type: NoteType,
  ownerUserId: string | undefined,
): ReportInputWave2 {
  return {
    title: form.title.trim(),
    periodMonth: form.month,
    purposeId: form.purposeId,
    noteType: type,
    ...(ownerUserId ? { ownerUserId } : {}),
    ...(type === 'travel' && form.tripScope !== ''
      ? { tripStart: form.tripStart, tripEnd: form.tripEnd, tripScope: form.tripScope }
      : {}),
  };
}

/** The problem codes the new-note sheet names in words (it/en under `reports.new.problems`). */
export const NEW_REPORT_PROBLEMS = [
  'PURPOSE_REQUIRED',
  'PURPOSE_NOT_ALLOWED',
  'NOTE_TYPE_NOT_ALLOWED_FOR_PURPOSE',
  'TRIP_DETAILS_REQUIRED',
  'TRIP_DATES_INVALID',
  'TRIP_ONLY_FOR_TRAVEL',
] as const;

export type NewReportProblem = (typeof NEW_REPORT_PROBLEMS)[number] | 'other';

export function problemKey(code: string): NewReportProblem {
  const known = NEW_REPORT_PROBLEMS.find((problem) => problem === code);
  return known ?? 'other';
}
